import { useState, type Ref } from 'react';
import { FaRegFilePdf, FaRegFileWord } from 'react-icons/fa6';
import { LuFileUp, LuX } from 'react-icons/lu';

import { cn } from '@mds/shared/lib/utils';

/** "240 KB", or "1.4 MB" from a megabyte up. */
const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const extensionOf = (name: string) => name.split('.').pop()?.toLowerCase() ?? '';

export type CvDropProps = {
  /** The file chosen, or nothing. `''` is what an empty `Field` hands over. */
  value?: File | null | '';
  onChange?: (file: File | null) => void;
  onBlur?: () => void;
  /** A file of the wrong type or size, with why. It is not taken. */
  onReject?: (message: string) => void;
  /** Extensions taken, without the dot. */
  extensions: readonly string[];
  maxBytes: number;
  /** Under the title while empty: "PDF or Word, up to 5 MB". */
  hint: string;
  id?: string;
  ref?: Ref<HTMLInputElement>;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
};

/**
 * One file, dropped or browsed for: a CV.
 *
 * Frontend only: the file is held in the form's state and nothing is uploaded
 * until the API has somewhere to send it.
 *
 * The whole zone is a `<label>` around a visually hidden file input, so a
 * click anywhere opens the picker and the keyboard reaches it as the input
 * itself; the zone draws the input's focus ring. Inside a `Field` it binds
 * like any control, and the Field's own label names the input too.
 *
 * A wrong type or a file over the limit is refused on the spot through
 * `onReject` rather than taken and failed later. The input is cleared after
 * every pick, so the same file can be chosen again after a remove.
 */
export default function CvDrop({
  value,
  onChange,
  onBlur,
  onReject,
  extensions,
  maxBytes,
  hint,
  id,
  ref,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: CvDropProps) {
  const [dragging, setDragging] = useState(false);
  const file = value instanceof File ? value : null;
  // The outline glyphs: the solid ones read heavy beside the light type.
  const FileMark = !file
    ? LuFileUp
    : extensionOf(file.name) === 'pdf'
      ? FaRegFilePdf
      : FaRegFileWord;

  const take = (picked: File | undefined) => {
    if (!picked) return;
    if (!extensions.includes(extensionOf(picked.name))) {
      onReject?.('Choose a PDF or Word file.');
      return;
    }
    if (picked.size > maxBytes) {
      onReject?.(`That file is over ${formatSize(maxBytes)}. Choose a smaller one.`);
      return;
    }
    onChange?.(picked);
  };

  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        // Leaving for one of its own children is not leaving.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        take(e.dataTransfer.files[0]);
      }}
      className={cn(
        'flex cursor-pointer items-center gap-4 rounded-card border-2 border-dashed p-5',
        'transition-[border-color,background-color,scale] duration-(--duration-fast) ease-standard',
        'motion-reduce:transition-none',
        'has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-focus',
        dragging
          ? 'scale-101 border-brand-edge bg-brand-wash'
          : file
            ? 'border-brand bg-surface'
            : invalid
              ? 'border-destructive bg-surface'
              : 'border-ink/20 bg-surface hover:border-brand-edge',
      )}
    >
      <input
        ref={ref}
        id={id}
        type="file"
        accept={extensions.map((ext) => `.${ext}`).join(',')}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        onBlur={onBlur}
        onChange={(e) => {
          take(e.target.files?.[0]);
          e.target.value = '';
        }}
        className="sr-only"
      />

      {/* The file's own icon once there is one, PDF or Word; an upload mark until then. */}
      <FileMark
        aria-hidden="true"
        className={cn(
          'size-10 shrink-0 transition-colors duration-(--duration-fast)',
          file ? 'text-brand-text' : 'text-ink-muted',
        )}
      />

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-md font-medium wrap-anywhere">
          {dragging ? 'Drop it here' : file ? file.name : 'Drop your CV or browse'}
        </span>
        <span className="text-sm text-ink-muted">
          {file ? `${formatSize(file.size)} · ready to send` : hint}
        </span>
      </span>

      {file && (
        <button
          type="button"
          aria-label="Remove CV"
          onClick={(e) => {
            // Inside the label: without this the click would open the picker.
            e.preventDefault();
            onChange?.(null);
          }}
          className={cn(
            'grid size-11 shrink-0 place-items-center rounded-full border border-hairline bg-surface text-ink',
            'transition-colors duration-(--duration-fast) hover:bg-panel hover:text-on-panel',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
          )}
        >
          <LuX aria-hidden="true" className="size-4" />
        </button>
      )}
    </label>
  );
}
